import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import { Drawer, Switch, Divider, Space, Button } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { useTheme } from '../../context/ThemeContext';
import ColorWheel from './color-wheel.component';

const ThemeSwitcher: React.FC = () => {
    const { darkMode, setDarkMode, primaryColor, setPrimaryColor } = useTheme();
    const [open, setOpen] = useState(false);

    const showDrawer = () => {
        setOpen(true);
    };

    const onClose = () => {
        setOpen(false);
    };

    // Circular reveal theme transition via the View Transitions API.
    // flushSync forces React (and antd cssinjs) to commit synchronously inside
    // the transition callback, so the "new" snapshot captures the applied theme.
    // Falls back to an instant swap when the API is missing or motion is reduced.
    const handleDarkToggle = (
        checked: boolean,
        event: React.MouseEvent | React.KeyboardEvent,
    ) => {
        const reduceMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)",
        ).matches;
        const doc = document as Document & {
            startViewTransition?: (
                cb: () => void,
            ) => { finished: Promise<void> };
        };

        if (!doc.startViewTransition || reduceMotion) {
            setDarkMode(checked);
            return;
        }

        // Keyboard toggles have no clientX/Y — fall back to viewport center.
        const x = "clientX" in event ? event.clientX : window.innerWidth / 2;
        const y = "clientY" in event ? event.clientY : window.innerHeight / 2;
        const endRadius = Math.hypot(
            Math.max(x, window.innerWidth - x),
            Math.max(y, window.innerHeight - y),
        );

        const root = document.documentElement;
        root.style.setProperty("--vt-x", `${x}px`);
        root.style.setProperty("--vt-y", `${y}px`);
        root.style.setProperty("--vt-r", `${endRadius}px`);

        const vt = doc.startViewTransition(() => {
            flushSync(() => {
                setDarkMode(checked);
            });
        });
        vt.finished.finally(() => {
            root.style.removeProperty("--vt-x");
            root.style.removeProperty("--vt-y");
            root.style.removeProperty("--vt-r");
        });
    };

    return (
        <>
            <Button
                type="text"
                icon={<SettingOutlined />}
                onClick={showDrawer}
                style={{ fontSize: '16px' }}
            />
            <Drawer
                title="Theme Settings"
                placement="right"
                onClose={onClose}
                open={open}
                width={320}
            >
                <div style={{ marginBottom: 24 }}>
                    <h3>Mode</h3>
                    <Space>
                        <span>Light</span>
                        <Switch
                            checked={darkMode}
                            onChange={handleDarkToggle}
                        />
                        <span>Dark</span>
                    </Space>
                </div>

                <Divider />

                <div style={{ marginBottom: 24 }}>
                    <h3>Primary Color</h3>
                    <ColorWheel value={primaryColor} onChange={setPrimaryColor} />
                </div>
            </Drawer>
            <style>{`
                /* Circular theme reveal — View Transitions API.
                   The "new" snapshot sits on top, clipped to a circle that expands
                   from the toggle click point (--vt-x/--vt-y) to the farthest
                   corner (--vt-r), revealing the new theme over the old one. */
                ::view-transition-old(root),
                ::view-transition-new(root) {
                    animation: none;
                    mix-blend-mode: normal;
                }
                ::view-transition-old(root) {
                    z-index: 1;
                }
                ::view-transition-new(root) {
                    z-index: 9999;
                    animation: themeReveal 0.5s cubic-bezier(0.22, 1, 0.36, 1);
                }
                @keyframes themeReveal {
                    from {
                        clip-path: circle(0px at var(--vt-x, 50%) var(--vt-y, 50%));
                    }
                    to {
                        clip-path: circle(var(--vt-r, 150%) at var(--vt-x, 50%) var(--vt-y, 50%));
                    }
                }
            `}</style>
        </>
    );
};

export default ThemeSwitcher;
